import React, { useState, useMemo, useEffect } from "react";
import BackButton from "../components/backButton";
import { TopAdminNav } from "../layouts/TopAdminNav";
import {
  FaMoneyBillWave,
  FaPlus,
  FaTrashAlt,
  FaSearch,
  FaFilter,
  FaChartPie,
  FaReceipt,
  FaFileInvoiceDollar,
  FaBoxes,
  FaArrowUp,
  FaInfoCircle,
  FaUndo,
  FaEdit,
  FaCalendarAlt,
  FaCalendarDay,
  FaTimes,
  FaDownload,
  FaUserTie,
  FaPhoneAlt,
  FaBuilding,
  FaStore,
  FaGasPump,
  FaBook,
  FaWrench,
  FaBus,
  FaSync,
  FaSpinner,
} from "react-icons/fa";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import {
  useGetExpenseCatalogQuery,
  useCreateExpenseCatalogMutation,
  useUpdateExpenseCatalogMutation,
  useDeleteExpenseCatalogMutation,
  useGetExpenseVendorsQuery,
  useCreateExpenseVendorMutation,
  useUpdateExpenseVendorMutation,
  useDeleteExpenseVendorMutation,
  useGetExpensesQuery,
  useCreateExpenseMutation,
  useDeleteExpenseMutation,
  useGetExpenseAnalyticsQuery,
} from "../hooks/useAdmin";
import {
  exportExpenseLedger,
  exportExpenseAnalytics,
  exportExpenseVendors,
} from "../services/admin";
import { useDebounce } from "../hooks/useDebounce";

// Data Types
export interface ExpenseVendorItem {
  id: string;
  name: string;
  categoryTag: string;
  category_tag?: string;
  phone: string;
  contactPerson?: string;
  contact_person?: string;
  bankName?: string;
  bank_name?: string;
  accountNumber?: string;
  account_number?: string;
  address?: string;
  notes?: string;
  total_outflows_paid?: number;
  transactions_count?: number;
}

export interface ExpenseCatalogItem {
  id: string;
  name: string;
  code?: string;
  description: string;
  iconType?: string;
  icon_type?: string;
  isActive?: boolean;
  is_active?: boolean;
  total_logged_outflow?: number;
  transactions_count?: number;
}

export interface ExpenseRecord {
  id: string;
  catalogId: string;
  categoryName: string;
  amount: number;
  expenseDate: string;
  paidTo: string;
  paymentMethod: "transfer" | "cash" | "pos" | "cheque";
  description: string;
  recordedBy: string;
  status: "verified" | "pending";
}

type ExpenseLedgerItemNorm = ExpenseRecord;


export const ExpenseManagement: React.FC = () => {
  // Navigation / Tabs: "dashboard" | "ledger" | "catalog" | "vendors"
  const [activeTab, setActiveTab] = useState<
    "dashboard" | "ledger" | "catalog" | "vendors"
  >("dashboard");

  // Filtering Controls State — declared early so queries can use them
  const [dateFilterMode, setDateFilterMode] = useState<
    "month" | "single" | "range" | "all"
  >("month");
  const [selectedMonth, setSelectedMonth] = useState<string>(() => String(new Date().getMonth() + 1).padStart(2, "0"));
  const [selectedYear, setSelectedYear] = useState<string>(() => String(new Date().getFullYear()));
  const [specificDate, setSpecificDate] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>("all");
  const [selectedVendorFilter, setSelectedVendorFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedSearch = useDebounce(searchQuery, 400);

  const [vendorSearchQuery, setVendorSearchQuery] = useState<string>("");
  const debouncedVendorSearch = useDebounce(vendorSearchQuery, 400);

  // Build server-side filter params
  const ledgerParams = useMemo(() => ({
    date_filter_mode: dateFilterMode,
    month: dateFilterMode === "month" ? selectedMonth : undefined,
    year: dateFilterMode === "month" ? selectedYear : undefined,
    specific_date: dateFilterMode === "single" ? specificDate : undefined,
    from_date: dateFilterMode === "range" ? startDate : undefined,
    to_date: dateFilterMode === "range" ? endDate : undefined,
    catalog_id: selectedCategoryFilter !== "all" ? selectedCategoryFilter : undefined,
    vendor: selectedVendorFilter !== "all" ? selectedVendorFilter : undefined,
    search: debouncedSearch || undefined,
  }), [dateFilterMode, selectedMonth, selectedYear, specificDate, startDate, endDate, selectedCategoryFilter, selectedVendorFilter, debouncedSearch]);

  const analyticsParams = useMemo(() => ({
    date_filter_mode: dateFilterMode,
    month: dateFilterMode === "month" ? selectedMonth : undefined,
    year: dateFilterMode === "month" ? selectedYear : undefined,
    specific_date: dateFilterMode === "single" ? specificDate : undefined,
    from_date: dateFilterMode === "range" ? startDate : undefined,
    to_date: dateFilterMode === "range" ? endDate : undefined,
    catalog_id: selectedCategoryFilter !== "all" ? selectedCategoryFilter : undefined,
    vendor: selectedVendorFilter !== "all" ? selectedVendorFilter : undefined,
  }), [dateFilterMode, selectedMonth, selectedYear, specificDate, startDate, endDate, selectedCategoryFilter, selectedVendorFilter]);

  // Real Backend API Catalog Query & Mutations
  const {
    data: catalogApiResponse,
    isLoading: isCatalogLoading,
    isFetching: isCatalogFetching,
    refetch: refetchCatalog,
  } = useGetExpenseCatalogQuery();

  const createCatalogMutation = useCreateExpenseCatalogMutation();
  const updateCatalogMutation = useUpdateExpenseCatalogMutation();
  const deleteCatalogMutation = useDeleteExpenseCatalogMutation();

  const createVendorMutation = useCreateExpenseVendorMutation();
  const updateVendorMutation = useUpdateExpenseVendorMutation();
  const deleteVendorMutation = useDeleteExpenseVendorMutation();

  // Server-side Expense Ledger
  const {
    data: ledgerApiResponse,
    isLoading: isLedgerLoading,
    isFetching: isLedgerFetching,
    refetch: refetchLedger,
  } = useGetExpensesQuery(ledgerParams);

  const createExpenseMutation = useCreateExpenseMutation();
  const deleteExpenseMutation = useDeleteExpenseMutation();

  // Server-side Expense Analytics
  const {
    data: analyticsApiResponse,
    isLoading: isAnalyticsLoading,
    refetch: refetchAnalytics,
  } = useGetExpenseAnalyticsQuery(analyticsParams);

  // Server-side Vendor query (with debounced search)
  const {
    data: vendorsApiResponse,
    isLoading: isVendorsLoading,
    isFetching: isVendorsFetching,
    refetch: refetchVendors,
  } = useGetExpenseVendorsQuery(debouncedVendorSearch ? { search: debouncedVendorSearch } : undefined);

  // Normalize API catalog items
  const catalog: ExpenseCatalogItem[] = useMemo(() => {
    const dataCandidate = catalogApiResponse?.data;
    const rawList = Array.isArray(dataCandidate)
      ? dataCandidate
      : Array.isArray(catalogApiResponse)
      ? catalogApiResponse
      : [];

    return rawList.map((item: any) => ({
      id: item.id,
      code: item.code || `EXP-${(item.name || "").slice(0, 4).toUpperCase()}`,
      name: item.name,
      description: item.description || "",
      iconType: item.icon_type || item.iconType || "projects",
      icon_type: item.icon_type || item.iconType || "projects",
      isActive:
        item.is_active !== undefined
          ? item.is_active
          : item.isActive !== undefined
          ? item.isActive
          : true,
      is_active:
        item.is_active !== undefined
          ? item.is_active
          : item.isActive !== undefined
          ? item.isActive
          : true,
      total_logged_outflow: Number(item.total_logged_outflow || 0),
      transactions_count: Number(item.transactions_count || 0),
    }));
  }, [catalogApiResponse]);

  // Derive server expenses list
  const expenses: ExpenseLedgerItemNorm[] = useMemo(() => {
    const raw = ledgerApiResponse?.data;
    if (!Array.isArray(raw)) return [];
    return raw.map((item: any) => ({
      id: String(item.id),
      catalogId: String(item.catalog_id),
      categoryName: item.category_name || "General",
      amount: Number(item.amount || 0),
      expenseDate: item.expense_date || "",
      paidTo: item.paid_to || "",
      paymentMethod: item.payment_method || "transfer",
      description: item.description || "",
      recordedBy: item.recorded_by || "",
      status: item.status || "verified",
    }));
  }, [ledgerApiResponse]);

  // Normalize API vendor items
  const vendors: ExpenseVendorItem[] = useMemo(() => {
    const dataCandidate = (vendorsApiResponse as any)?.data;
    const rawList = Array.isArray(dataCandidate)
      ? dataCandidate
      : Array.isArray(vendorsApiResponse)
      ? vendorsApiResponse
      : [];

    return rawList.map((item: any) => ({
      id: item.id,
      name: item.name || "",
      categoryTag: item.category_tag || item.categoryTag || "General Supplier",
      category_tag: item.category_tag || item.categoryTag || "General Supplier",
      phone: item.phone || "N/A",
      contactPerson: item.contact_person || item.contactPerson || "",
      contact_person: item.contact_person || item.contactPerson || "",
      bankName: item.bank_name || item.bankName || "",
      bank_name: item.bank_name || item.bankName || "",
      accountNumber: item.account_number || item.accountNumber || "",
      account_number: item.account_number || item.accountNumber || "",
      address: item.address || "",
      notes: item.notes || "",
      total_outflows_paid: Number(item.total_outflows_paid || 0),
      transactions_count: Number(item.transactions_count || 0),
    }));
  }, [vendorsApiResponse]);

  // Clean up legacy localStorage data on mount
  useEffect(() => {
    try {
      localStorage.removeItem("pendulum_expense_catalog_data");
      localStorage.removeItem("pendulum_expense_records_data");
      localStorage.removeItem("pendulum_expense_vendors_data");
    } catch { /* ignore */ }
  }, []);

  // Modals State
  const [openAddExpenseModal, setOpenAddExpenseModal] = useState(false);
  const [openAddCatalogModal, setOpenAddCatalogModal] = useState(false);
  const [openAddVendorModal, setOpenAddVendorModal] = useState(false);
  const [openEditVendorModal, setOpenEditVendorModal] = useState(false);
  const [editingVendorItem, setEditingVendorItem] =
    useState<ExpenseVendorItem | null>(null);

  // New Vendor Form State
  const [newVendorForm, setNewVendorForm] = useState({
    name: "",
    categoryTag: "",
    phone: "",
    contactPerson: "",
    bankName: "",
    accountNumber: "",
    address: "",
    notes: "",
  });

  // Edit Vendor Form State
  const [editVendorForm, setEditVendorForm] = useState({
    name: "",
    categoryTag: "",
    phone: "",
    contactPerson: "",
    bankName: "",
    accountNumber: "",
    address: "",
    notes: "",
  });

  // New Expense Form State
  const [newExpenseForm, setNewExpenseForm] = useState<{
    catalogId: string;
    amount: string;
    expenseDate: string;
    vendorId: string;
    paymentMethod: "transfer" | "cash" | "pos" | "cheque";
    description: string;
  }>({
    catalogId: "",
    amount: "",
    expenseDate: new Date().toISOString().split("T")[0],
    vendorId: "",
    paymentMethod: "transfer",
    description: "",
  });

  const STANDARD_ICON_TYPES = [
    "projects",
    "fuel",
    "books",
    "maintenance",
    "rent",
    "supplies",
    "transport",
    "general",
  ];

  // New Catalog Form State
  const [newCatalogForm, setNewCatalogForm] = useState({
    name: "",
    code: "",
    description: "",
    icon_type: "projects",
    other_type_description: "",
  });

  // Edit Catalog Form State
  const [openEditCatalogModal, setOpenEditCatalogModal] = useState(false);
  const [editingCatalogItem, setEditingCatalogItem] =
    useState<ExpenseCatalogItem | null>(null);
  const [editCatalogForm, setEditCatalogForm] = useState({
    name: "",
    code: "",
    description: "",
    icon_type: "projects",
    other_type_description: "",
  });

  // All filtering is server-side — derive display values from API responses
  const filteredExpenses = expenses; // already filtered server-side
  const totalFilteredAmount = ledgerApiResponse?.meta?.total_filtered_amount ?? 0;
  const totalFilteredCount = ledgerApiResponse?.meta?.total_records ?? 0;

  const analyticsData = analyticsApiResponse?.data;
  const todaysTotal = analyticsData?.metrics?.todays_outflow ?? 0;
  const categoryAnalytics = useMemo(() => (analyticsData?.category_breakdown ?? []).map((c: any) => ({
    catalogId: String(c.catalog_id),
    name: c.name,
    total: Number(c.total_amount),
    count: Number(c.transactions_count),
    percentage: Number(c.percentage_share),
  })), [analyticsData]);
  const highestSpendCategory = analyticsData?.metrics?.highest_spend_category ?? null;

  // Format Currency
  const formatNaira = (val: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const MONTH_NAMES: { [key: string]: string } = {
    "01": "January",
    "02": "February",
    "03": "March",
    "04": "April",
    "05": "May",
    "06": "June",
    "07": "July",
    "08": "August",
    "09": "September",
    "10": "October",
    "11": "November",
    "12": "December",
  };

  const formatFriendlyDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      const parts = dateStr.split("-");
      if (parts.length === 3) {
        const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        return d.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  // Handlers
  const handleCreateExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newExpenseForm.catalogId) {
      toast.error("Please select an expense category");
      return;
    }
    const numAmount = Number(newExpenseForm.amount);
    if (!numAmount || numAmount <= 0) {
      toast.error("Please enter a valid expense amount");
      return;
    }
    if (!newExpenseForm.vendorId) {
      toast.error("Please select a vendor / supplier");
      return;
    }
    try {
      await createExpenseMutation.mutateAsync({
        catalog_id: newExpenseForm.catalogId,
        vendor_id: newExpenseForm.vendorId,
        amount: numAmount,
        expense_date: newExpenseForm.expenseDate,
        payment_method: newExpenseForm.paymentMethod,
        description: newExpenseForm.description.trim() || undefined,
      });
      toast.success("Daily expense recorded successfully!");
      setOpenAddExpenseModal(false);
      setNewExpenseForm({
        catalogId: "",
        amount: "",
        expenseDate: new Date().toISOString().split("T")[0],
        vendorId: "",
        paymentMethod: "transfer",
        description: "",
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to record expense");
    }
  };

  const getCategoryIcon = (iconType?: string) => {
    const t = (iconType || "").toLowerCase();
    if (t.includes("fuel")) return <FaGasPump className="text-amber-500" />;
    if (t.includes("book")) return <FaBook className="text-indigo-500" />;
    if (t.includes("project")) return <FaBoxes className="text-sky-500" />;
    if (t.includes("maint")) return <FaWrench className="text-rose-500" />;
    if (t.includes("rent")) return <FaBuilding className="text-emerald-500" />;
    if (t.includes("suppl")) return <FaBoxes className="text-teal-500" />;
    if (t.includes("transport") || t.includes("bus")) return <FaBus className="text-cyan-500" />;
    return <FaReceipt className="text-indigo-600" />;
  };

  const handleCreateCatalogItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCatalogForm.name.trim()) {
      toast.error("Please enter an expense category name");
      return;
    }

    if (
      newCatalogForm.icon_type === "others" &&
      !newCatalogForm.other_type_description?.trim()
    ) {
      toast.error("Please describe your custom classification");
      return;
    }

    const finalIconType =
      newCatalogForm.icon_type === "others"
        ? newCatalogForm.other_type_description?.trim() || "others"
        : newCatalogForm.icon_type;

    try {
      await createCatalogMutation.mutateAsync({
        name: newCatalogForm.name.trim(),
        description: newCatalogForm.description.trim(),
        icon_type: finalIconType || "projects",
      });
      setOpenAddCatalogModal(false);
      setNewCatalogForm({
        name: "",
        code: "",
        description: "",
        icon_type: "projects",
        other_type_description: "",
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenEditCatalog = (cat: ExpenseCatalogItem) => {
    setEditingCatalogItem(cat);
    const currentIcon = cat.icon_type || cat.iconType || "projects";
    const isStandard = STANDARD_ICON_TYPES.includes(currentIcon);

    setEditCatalogForm({
      name: cat.name,
      code: cat.code || "",
      description: cat.description,
      icon_type: isStandard ? currentIcon : "others",
      other_type_description: isStandard
        ? ""
        : currentIcon === "others"
        ? ""
        : currentIcon,
    });
    setOpenEditCatalogModal(true);
  };

  const handleSaveEditCatalog = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCatalogItem) return;
    if (!editCatalogForm.name.trim()) {
      toast.error("Please enter an expense category name");
      return;
    }

    if (
      editCatalogForm.icon_type === "others" &&
      !editCatalogForm.other_type_description?.trim()
    ) {
      toast.error("Please describe your custom classification");
      return;
    }

    const finalIconType =
      editCatalogForm.icon_type === "others"
        ? editCatalogForm.other_type_description?.trim() || "others"
        : editCatalogForm.icon_type;

    try {
      await updateCatalogMutation.mutateAsync({
        id: editingCatalogItem.id,
        payload: {
          name: editCatalogForm.name.trim(),
          description: editCatalogForm.description.trim(),
          icon_type: finalIconType || "projects",
        },
      });
      setOpenEditCatalogModal(false);
      setEditingCatalogItem(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteCatalog = (catId: string, catName: string) => {
    Swal.fire({
      title: "Delete Expense Category?",
      html: `Are you sure you want to delete <b>"${catName}"</b> from the expense catalog?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete Category",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await deleteCatalogMutation.mutateAsync(catId);
        } catch (err) {
          console.error(err);
        }
      }
    });
  };

  const handleDeleteExpense = (id: string, name: string, amt: number) => {
    Swal.fire({
      title: "Delete Expense Record?",
      text: `Are you sure you want to delete ${formatNaira(amt)} logged for ${name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await deleteExpenseMutation.mutateAsync(id);
          toast.success("Expense entry removed");
        } catch (err: any) {
          toast.error(err?.response?.data?.message || "Failed to delete expense");
        }
      }
    });
  };

  const handleExportLedger = async () => {
    const toastId = toast.loading("Preparing ledger export...");
    try {
      await exportExpenseLedger(ledgerParams);
      toast.success("Ledger exported successfully", { id: toastId });
    } catch {
      toast.error("Failed to export ledger", { id: toastId });
    }
  };

  const handleExportAnalytics = async () => {
    const toastId = toast.loading("Preparing analytics export...");
    try {
      await exportExpenseAnalytics(analyticsParams);
      toast.success("Analytics exported successfully", { id: toastId });
    } catch {
      toast.error("Failed to export analytics", { id: toastId });
    }
  };

  const handleExportVendors = async () => {
    const toastId = toast.loading("Preparing vendors export...");
    try {
      await exportExpenseVendors();
      toast.success("Vendors directory exported successfully", { id: toastId });
    } catch {
      toast.error("Failed to export vendors", { id: toastId });
    }
  };

  // Vendor Management Handlers
  const handleCreateVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVendorForm.name.trim()) {
      toast.error("Please enter vendor / supplier name");
      return;
    }

    try {
      await createVendorMutation.mutateAsync({
        name: newVendorForm.name.trim(),
        category_tag:
          newVendorForm.categoryTag.trim() || "General Supplier",
        phone: newVendorForm.phone.trim(),
        contact_person: newVendorForm.contactPerson.trim(),
        bank_name: newVendorForm.bankName.trim(),
        account_number: newVendorForm.accountNumber.trim(),
        address: newVendorForm.address.trim(),
        notes: newVendorForm.notes.trim(),
      });
      setOpenAddVendorModal(false);
      setNewVendorForm({
        name: "",
        categoryTag: "",
        phone: "",
        contactPerson: "",
        bankName: "",
        accountNumber: "",
        address: "",
        notes: "",
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenEditVendor = (vendor: ExpenseVendorItem) => {
    setEditingVendorItem(vendor);
    setEditVendorForm({
      name: vendor.name,
      categoryTag: vendor.categoryTag || vendor.category_tag || "",
      phone: vendor.phone || "",
      contactPerson: vendor.contactPerson || vendor.contact_person || "",
      bankName: vendor.bankName || vendor.bank_name || "",
      accountNumber: vendor.accountNumber || vendor.account_number || "",
      address: vendor.address || "",
      notes: vendor.notes || "",
    });
    setOpenEditVendorModal(true);
  };

  const handleSaveEditVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingVendorItem) return;
    if (!editVendorForm.name.trim()) {
      toast.error("Please enter vendor / supplier name");
      return;
    }

    try {
      await updateVendorMutation.mutateAsync({
        id: editingVendorItem.id,
        payload: {
          name: editVendorForm.name.trim(),
          category_tag: editVendorForm.categoryTag.trim() || "General Supplier",
          phone: editVendorForm.phone.trim(),
          contact_person: editVendorForm.contactPerson.trim(),
          bank_name: editVendorForm.bankName.trim(),
          account_number: editVendorForm.accountNumber.trim(),
          address: editVendorForm.address.trim(),
          notes: editVendorForm.notes.trim(),
        },
      });
      setOpenEditVendorModal(false);
      setEditingVendorItem(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteVendor = (vendorId: string, vendorName: string) => {
    Swal.fire({
      title: "Delete Vendor / Supplier?",
      html: `Are you sure you want to remove <b>"${vendorName}"</b> from the suppliers directory?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete Vendor",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await deleteVendorMutation.mutateAsync(vendorId);
          if (selectedVendorFilter.toLowerCase() === vendorName.toLowerCase()) {
            setSelectedVendorFilter("all");
          }
        } catch (err) {
          console.error(err);
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 space-y-6 text-left">
      {/* 1. Header Navigation Bar */}
      <div className="w-full flex items-center justify-between">
        <BackButton />
        <TopAdminNav
          name="School Financial Director"
          title="Operational Expense Tracker"
        />
      </div>

      {/* 2. Overview Banner Card */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-2xl bg-[#0096D1]/10 text-[#0096D1] flex items-center justify-center font-bold text-2xl shadow-xs">
            <FaFileInvoiceDollar />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-black text-slate-800 tracking-tight">
                Operational Expense Tracker
              </h1>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                Internal Audit
              </span>
            </div>
            <p className="text-xs font-semibold text-slate-400 mt-1 flex items-center gap-2">
              <span>Track recurring school expenditures</span> •
              <span className="text-slate-600 font-bold">
                Zero Fixed Prices on Catalog
              </span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto justify-end">
          <button
            type="button"
            onClick={() => refetchCatalog()}
            title="Refresh expense catalog from server"
            className="px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 text-xs font-bold transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer active:scale-95"
          >
            <FaSync className={`text-[11px] ${isCatalogFetching ? "animate-spin text-[#0096D1]" : ""}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => setOpenAddExpenseModal(true)}
            className="px-5 py-2.5 rounded-xl bg-[#0096D1] hover:bg-[#0082b5] text-white text-xs font-bold shadow-md hover:shadow-lg transition-all active:scale-95 flex items-center gap-2 cursor-pointer"
          >
            <FaPlus /> Log Daily Expense
          </button>
        </div>
      </div>

      {/* 3. Top Metrics Cards Deck */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Metric 1: Total Period Expenses */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Total Filtered Outflows
            </span>
            <div className="h-8 w-8 rounded-xl bg-blue-50 text-[#0096D1] flex items-center justify-center text-sm">
              <FaMoneyBillWave />
            </div>
          </div>
          <p className="text-2xl font-black text-slate-800 font-mono mt-3">
            {isLedgerLoading ? "..." : formatNaira(totalFilteredAmount)}
          </p>
          <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-2 font-medium">
            <span className="inline-flex items-center text-emerald-600 font-bold font-mono">
              <FaArrowUp className="text-[10px] mr-1" />
              {totalFilteredCount} transactions
            </span>
            <span>
              in{" "}
              {dateFilterMode === "single" && specificDate
                ? `specific date (${formatFriendlyDate(specificDate)})`
                : dateFilterMode === "range" && (startDate || endDate)
                ? `date range (${startDate || "Start"} to ${endDate || "Today"})`
                : `${selectedMonth === "all" ? "all months" : MONTH_NAMES[selectedMonth]} ${selectedYear}`}
            </span>
          </div>
        </div>

        {/* Metric 2: Highest Spend Category */}
        <div className="bg-white rounded-2xl border border-amber-100 shadow-sm p-5 relative overflow-hidden bg-gradient-to-br from-white to-amber-50/20">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1">
              Top Outflow Area (Highest)
            </span>
            <div className="h-8 w-8 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center text-sm">
              <FaChartPie />
            </div>
          </div>
          <p className="text-lg font-black text-slate-800 truncate mt-3">
            {highestSpendCategory
              ? highestSpendCategory.name
              : "No records found"}
          </p>
          <p className="text-xs text-slate-500 mt-1 font-semibold">
            {highestSpendCategory ? (
              <>
                <span className="font-mono font-bold text-amber-700">
                  {formatNaira(highestSpendCategory.total_amount ?? 0)}
                </span>{" "}
                ({(highestSpendCategory.percentage_share ?? 0).toFixed(1)}% of overall spend)
              </>
            ) : (
              "N/A"
            )}
          </p>
        </div>

        {/* Metric 3: Today's Outflow */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 relative overflow-hidden sm:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Today's Outflows
            </span>
            <div className="h-8 w-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-sm">
              <FaReceipt />
            </div>
          </div>
          <p className="text-2xl font-black text-slate-800 font-mono mt-3">
            {isAnalyticsLoading ? "..." : formatNaira(todaysTotal)}
          </p>
          <p className="text-xs text-slate-400 mt-2 font-medium">
            Daily transactions recorded today ({new Date().toLocaleDateString()})
          </p>
        </div>
      </div>

      {/* 4. Tab Switcher Navigation */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center bg-slate-100/70 p-1 rounded-xl gap-1 flex-wrap">
          <button
            type="button"
            onClick={() => setActiveTab("dashboard")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === "dashboard"
                ? "bg-white text-slate-900 shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <FaChartPie /> Spend Analytics
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("ledger")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === "ledger"
                ? "bg-white text-slate-900 shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <FaReceipt /> Daily Expense Ledger ({totalFilteredCount})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("catalog")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === "catalog"
                ? "bg-white text-slate-900 shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <FaBoxes /> Expense Catalog ({catalog.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("vendors")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === "vendors"
                ? "bg-white text-slate-900 shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <FaBuilding /> Suppliers / Vendors ({vendors.length})
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {activeTab === "ledger" && (
            <button
              type="button"
              onClick={handleExportLedger}
              className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
            >
              <FaDownload className="text-[11px] text-[#0096D1]" /> Export Ledger (CSV)
            </button>
          )}

          {activeTab === "dashboard" && (
            <button
              type="button"
              onClick={handleExportAnalytics}
              className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
            >
              <FaDownload className="text-[11px] text-[#0096D1]" /> Export Breakdown (CSV)
            </button>
          )}

          {activeTab === "catalog" && (
            <button
              type="button"
              onClick={() => setOpenAddCatalogModal(true)}
              className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold transition-all flex items-center gap-2 cursor-pointer"
            >
              <FaPlus className="text-[10px]" /> Add Catalog Head
            </button>
          )}

          {activeTab === "vendors" && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExportVendors}
                className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
              >
                <FaDownload className="text-[11px] text-emerald-600" /> Export Suppliers (CSV)
              </button>
              <button
                type="button"
                onClick={() => setOpenAddVendorModal(true)}
                className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold transition-all flex items-center gap-2 cursor-pointer"
              >
                <FaPlus className="text-[10px]" /> Add Supplier / Vendor
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 5. Filters Bar (Visible only for Dashboard and Ledger) */}
      {(activeTab === "dashboard" || activeTab === "ledger") && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3.5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <FaFilter className="text-[#0096D1]" /> Filter Ledger Data:
            </div>

          {/* Quick Presets */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1">
              Quick:
            </span>
            <button
              type="button"
              onClick={() => {
                const today = new Date().toISOString().split("T")[0];
                setDateFilterMode("single");
                setSpecificDate(today);
                toast.success("Filtered for Today");
              }}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                dateFilterMode === "single" &&
                specificDate === new Date().toISOString().split("T")[0]
                  ? "bg-[#0096D1] text-white shadow-xs"
                  : "bg-slate-100 hover:bg-slate-200/70 text-slate-600"
              }`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                setDateFilterMode("month");
                setSelectedMonth(String(now.getMonth() + 1).padStart(2, "0"));
                setSelectedYear(String(now.getFullYear()));
                setSpecificDate("");
                setStartDate("");
                setEndDate("");
                toast.success(`Filtered for ${now.toLocaleString("default", { month: "long" })} ${now.getFullYear()}`);
              }}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                dateFilterMode === "month"
                  ? "bg-slate-800 text-white shadow-xs"
                  : "bg-slate-100 hover:bg-slate-200/70 text-slate-600"
              }`}
            >
              This Month
            </button>
            <button
              type="button"
              onClick={() => {
                setDateFilterMode("all");
                setSpecificDate("");
                setStartDate("");
                setEndDate("");
                setSelectedCategoryFilter("all");
                setSearchQuery("");
                toast.success("Showing All Records");
              }}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                dateFilterMode === "all"
                  ? "bg-slate-800 text-white shadow-xs"
                  : "bg-slate-100 hover:bg-slate-200/70 text-slate-600"
              }`}
            >
              All Records
            </button>
          </div>
        </div>

        {/* Controls Row */}
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
          {/* Search Box */}
          <div className="relative min-w-[200px] flex-1 max-w-xs">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
            <input
              type="text"
              placeholder="Search vendor, note, catalog..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full text-xs font-semibold pl-9 pr-3 py-2 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
            />
          </div>

          {/* Date Filter Mode Selector */}
          <div className="flex items-center gap-1 bg-slate-100/80 p-1 rounded-xl">
            <button
              type="button"
              onClick={() => setDateFilterMode("month")}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                dateFilterMode === "month"
                  ? "bg-white text-slate-900 shadow-2xs"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Month View
            </button>
            <button
              type="button"
              onClick={() => {
                setDateFilterMode("single");
                if (!specificDate) {
                  setSpecificDate(new Date().toISOString().split("T")[0]);
                }
              }}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                dateFilterMode === "single"
                  ? "bg-white text-slate-900 shadow-2xs"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Specific Date
            </button>
            <button
              type="button"
              onClick={() => setDateFilterMode("range")}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                dateFilterMode === "range"
                  ? "bg-white text-slate-900 shadow-2xs"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Date Range (From - To)
            </button>
          </div>

          {/* Dynamic Date Controls */}
          {dateFilterMode === "month" && (
            <div className="flex items-center gap-2">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="text-xs font-bold px-3 py-2 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
              >
                <option value="all">All Months</option>
                <option value="01">January</option>
                <option value="02">February</option>
                <option value="03">March</option>
                <option value="04">April</option>
                <option value="05">May</option>
                <option value="06">June</option>
                <option value="07">July</option>
                <option value="08">August</option>
                <option value="09">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>

              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="text-xs font-bold px-3 py-2 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
              >
                <option value="all">All Years</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
              </select>
            </div>
          )}

          {dateFilterMode === "single" && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200">
                <FaCalendarDay className="text-[#0096D1] text-xs" />
                <input
                  type="date"
                  value={specificDate}
                  onChange={(e) => setSpecificDate(e.target.value)}
                  className="text-xs font-bold bg-transparent text-slate-800 focus:outline-none font-mono cursor-pointer"
                />
              </div>
              {specificDate && (
                <button
                  type="button"
                  onClick={() => setSpecificDate("")}
                  className="text-xs text-slate-400 hover:text-rose-600 font-bold p-1 cursor-pointer"
                  title="Clear single date"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          )}

          {dateFilterMode === "range" && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 rounded-xl border border-slate-200">
                <span className="text-[10px] uppercase font-bold text-slate-400">
                  From:
                </span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="text-xs font-bold bg-transparent text-slate-800 focus:outline-none font-mono cursor-pointer"
                />
              </div>

              <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1.5 rounded-xl border border-slate-200">
                <span className="text-[10px] uppercase font-bold text-slate-400">
                  To:
                </span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="text-xs font-bold bg-transparent text-slate-800 focus:outline-none font-mono cursor-pointer"
                />
              </div>

              {(startDate || endDate) && (
                <button
                  type="button"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                  className="text-xs text-slate-400 hover:text-rose-600 font-bold p-1 cursor-pointer"
                  title="Clear date range"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          )}

          {/* Category Filter */}
          <select
            value={selectedCategoryFilter}
            onChange={(e) => setSelectedCategoryFilter(e.target.value)}
            className="text-xs font-bold px-3 py-2 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer max-w-[180px] truncate"
          >
            <option value="all">All Catalog Heads</option>
            {catalog.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* Vendor / Supplier Filter */}
          <select
            value={selectedVendorFilter}
            onChange={(e) => setSelectedVendorFilter(e.target.value)}
            className="text-xs font-bold px-3 py-2 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer max-w-[190px] truncate"
          >
            <option value="all">All Vendors / Payees</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.name}>
                {v.name}
              </option>
            ))}
          </select>
        </div>

        {/* Visual Active Filter Indicator Bar */}
        {selectedVendorFilter !== "all" && (
          <div className="flex flex-wrap items-center justify-between gap-2.5 p-3 bg-purple-50/90 border border-purple-200 rounded-xl text-purple-900 text-xs animate-fade-in">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex items-center justify-center h-6 w-6 rounded-lg bg-purple-600 text-white shrink-0">
                <FaStore className="text-xs" />
              </span>
              <span className="font-extrabold text-purple-700 uppercase tracking-wider text-[11px]">
                Filtered By Supplier / Payee:
              </span>
              <span className="bg-white px-2.5 py-0.5 rounded-md border border-purple-300 font-black font-mono text-slate-800 text-xs shadow-2xs">
                {selectedVendorFilter}
              </span>
              <span className="text-slate-500 font-medium">
                • Showing <b>{totalFilteredCount}</b> transaction{totalFilteredCount === 1 ? "" : "s"} totaling{" "}
                <span className="font-mono font-bold text-slate-800">
                  {formatNaira(totalFilteredAmount)}
                </span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedVendorFilter("all");
                toast.success("Vendor filter cleared");
              }}
              className="px-2.5 py-1 rounded-lg bg-white hover:bg-rose-50 text-rose-600 border border-rose-200 text-xs font-bold transition-all flex items-center gap-1 cursor-pointer shadow-2xs"
            >
              <FaTimes className="text-[10px]" /> Clear Vendor
            </button>
          </div>
        )}

        {dateFilterMode === "single" && specificDate && (
          <div className="flex flex-wrap items-center justify-between gap-2.5 p-3 bg-sky-50/90 border border-sky-200 rounded-xl text-sky-900 text-xs animate-fade-in">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex items-center justify-center h-6 w-6 rounded-lg bg-[#0096D1] text-white shrink-0">
                <FaCalendarDay className="text-xs" />
              </span>
              <span className="font-extrabold text-[#0096D1] uppercase tracking-wider text-[11px]">
                Filtered By Specific Date:
              </span>
              <span className="bg-white px-2.5 py-0.5 rounded-md border border-sky-300 font-black font-mono text-slate-800 text-xs shadow-2xs">
                {formatFriendlyDate(specificDate)}
              </span>
              <span className="text-slate-500 font-medium">
                • Showing <b>{totalFilteredCount}</b> transaction{totalFilteredCount === 1 ? "" : "s"} totaling{" "}
                <span className="font-mono font-bold text-slate-800">
                  {formatNaira(totalFilteredAmount)}
                </span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setDateFilterMode("month");
                setSpecificDate("");
                toast.success("Date filter cleared");
              }}
              className="px-2.5 py-1 rounded-lg bg-white hover:bg-rose-50 text-rose-600 border border-rose-200 text-xs font-bold transition-all flex items-center gap-1 cursor-pointer shadow-2xs"
            >
              <FaTimes className="text-[10px]" /> Clear Date Filter
            </button>
          </div>
        )}

        {dateFilterMode === "range" && (startDate || endDate) && (
          <div className="flex flex-wrap items-center justify-between gap-2.5 p-3 bg-emerald-50/90 border border-emerald-200 rounded-xl text-emerald-900 text-xs animate-fade-in">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex items-center justify-center h-6 w-6 rounded-lg bg-emerald-600 text-white shrink-0">
                <FaCalendarAlt className="text-xs" />
              </span>
              <span className="font-extrabold text-emerald-700 uppercase tracking-wider text-[11px]">
                Filtered By Date Range:
              </span>
              <span className="bg-white px-2.5 py-0.5 rounded-md border border-emerald-300 font-black font-mono text-slate-800 text-xs shadow-2xs">
                {startDate ? formatFriendlyDate(startDate) : "Any"} →{" "}
                {endDate ? formatFriendlyDate(endDate) : "Today"}
              </span>
              <span className="text-slate-500 font-medium">
                • Showing <b>{totalFilteredCount}</b> transaction{totalFilteredCount === 1 ? "" : "s"} totaling{" "}
                <span className="font-mono font-bold text-slate-800">
                  {formatNaira(totalFilteredAmount)}
                </span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setDateFilterMode("month");
                setStartDate("");
                setEndDate("");
                toast.success("Range filter cleared");
              }}
              className="px-2.5 py-1 rounded-lg bg-white hover:bg-rose-50 text-rose-600 border border-rose-200 text-xs font-bold transition-all flex items-center gap-1 cursor-pointer shadow-2xs"
            >
              <FaTimes className="text-[10px]" /> Clear Range Filter
            </button>
          </div>
        )}

        {dateFilterMode === "month" && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 bg-slate-50/80 border border-slate-200/60 rounded-xl text-slate-500 text-[11px]">
            <div className="flex items-center gap-2">
              <FaCalendarAlt className="text-slate-400 text-xs" />
              <span className="font-bold text-slate-700">Active View:</span>
              <span className="font-bold text-slate-800">
                {selectedMonth === "all"
                  ? "All Months"
                  : MONTH_NAMES[selectedMonth]}{" "}
                {selectedYear === "all" ? "(All Years)" : selectedYear}
              </span>
              <span className="text-slate-400">
                • {totalFilteredCount} transaction{totalFilteredCount === 1 ? "" : "s"} logged ({formatNaira(totalFilteredAmount)})
              </span>
            </div>
          </div>
        )}
      </div>
      )}

      {/* 6. TAB CONTENTS */}

      {/* TAB A: Spend Analytics & Monthly Aggregation */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          {/* Category Breakdown Progress Grid */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
              <div>
                <h3 className="text-base font-extrabold text-slate-800 tracking-tight">
                  Fund Outflow Distribution by Category
                </h3>
                <p className="text-xs font-medium text-slate-400 mt-0.5">
                  Breakdown answering where the school spends the most funds
                </p>
              </div>
              <span className="text-xs font-mono font-bold bg-[#0096D1]/10 text-[#0096D1] px-3 py-1 rounded-full self-start sm:self-auto">
                {isAnalyticsLoading ? "..." : `${categoryAnalytics.length} active spending heads`}
              </span>
            </div>

            {categoryAnalytics.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {categoryAnalytics.map((cat, idx) => (
                  <div
                    key={cat.catalogId}
                    className="p-4 rounded-xl bg-slate-50/60 border border-slate-100 space-y-3 hover:border-slate-200 transition-all"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="h-6 w-6 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-500 font-mono">
                          {idx + 1}
                        </span>
                        <span className="text-sm font-bold text-slate-800 truncate">
                          {cat.name}
                        </span>
                      </div>
                      <span className="text-sm font-black font-mono text-slate-800">
                        {formatNaira(cat.total)}
                      </span>
                    </div>

                    {/* Progress Bar Meter */}
                    <div className="space-y-1">
                      <div className="w-full bg-slate-200/80 h-2.5 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            idx === 0
                              ? "bg-amber-500"
                              : idx === 1
                              ? "bg-[#0096D1]"
                              : idx === 2
                              ? "bg-emerald-500"
                              : "bg-purple-500"
                          }`}
                          style={{ width: `${Math.min(cat.percentage, 100)}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-400 font-medium">
                        <span>{cat.count} recorded transactions</span>
                        <span className="font-bold text-slate-600 font-mono">
                          {cat.percentage}% of total outflows
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center text-slate-400 font-medium text-xs italic">
                No expense entries found matching the active filter parameters.
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB B: Daily Expense Ledger Table */}
      {activeTab === "ledger" && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm font-extrabold text-slate-800 tracking-tight">
                  Daily Outflow Transaction Records
                </h2>
                {dateFilterMode === "single" && specificDate && (
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-[#0096D1]/10 text-[#0096D1] border border-[#0096D1]/25 flex items-center gap-1">
                    <FaCalendarDay className="text-[9px]" /> {formatFriendlyDate(specificDate)}
                  </span>
                )}
                {dateFilterMode === "range" && (startDate || endDate) && (
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                    <FaCalendarAlt className="text-[9px]" /> {startDate || "Start"} → {endDate || "Today"}
                  </span>
                )}
              </div>
              <p className="text-[11px] font-medium text-slate-400 mt-0.5">
                Detailed day-to-day operational audit trail
              </p>
            </div>
            <span className="text-xs font-mono font-bold text-slate-700 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-2xs">
              Total Filtered: {formatNaira(totalFilteredAmount)}
            </span>
          </div>

          <div className="overflow-x-auto w-full">
            <table className="w-full border-collapse text-left min-w-[750px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                  <th className="py-4 px-6 w-[15%]">Date</th>
                  <th className="py-4 px-4 w-[25%]">Catalog Category</th>
                  <th className="py-4 px-4 w-[20%]">Paid To (Beneficiary)</th>
                  <th className="py-4 px-4 w-[15%]">Amount</th>
                  <th className="py-4 px-4 w-[15%]">Channel</th>
                  <th className="py-4 px-4 w-[10%] text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLedgerLoading || isLedgerFetching ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center">
                      <FaSpinner className="animate-spin text-[#0096D1] text-xl mx-auto" />
                      <p className="text-xs text-slate-400 mt-2 font-medium">Loading expense records...</p>
                    </td>
                  </tr>
                ) : expenses.length > 0 ? (
                  expenses.map((exp) => (
                    <tr
                      key={exp.id}
                      className="group bg-white hover:bg-slate-50/30 transition-colors text-xs font-semibold text-slate-600"
                    >
                      <td className="py-3.5 px-6 font-mono text-slate-600">
                        <button
                          type="button"
                          onClick={() => {
                            setDateFilterMode("single");
                            setSpecificDate(exp.expenseDate);
                            toast.success(`Filtered for ${exp.expenseDate}`);
                          }}
                          title="Click to filter ledger by this specific date"
                          className="hover:text-[#0096D1] hover:underline cursor-pointer flex items-center gap-1.5 text-slate-700 group-hover:text-[#0096D1] transition-colors"
                        >
                          <FaCalendarDay className="text-[10px] text-slate-400 group-hover:text-[#0096D1]" />
                          <span>{exp.expenseDate}</span>
                        </button>
                      </td>
                      <td className="py-3.5 px-4 font-bold text-slate-800">
                        <div className="truncate">{exp.categoryName}</div>
                        {exp.description && (
                          <div className="text-[10px] text-slate-400 font-normal italic truncate mt-0.5">
                            "{exp.description}"
                          </div>
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-slate-700 truncate">
                        {exp.paidTo}
                      </td>
                      <td className="py-3.5 px-4 font-mono font-black text-slate-900">
                        {formatNaira(exp.amount)}
                      </td>
                      <td className="py-3.5 px-4 uppercase font-mono text-[10px]">
                        <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-bold border border-slate-200">
                          {exp.paymentMethod}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <button
                          type="button"
                          onClick={() => handleDeleteExpense(exp.id, exp.categoryName, exp.amount)}
                          title="Delete entry"
                          className="h-7 w-7 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all inline-flex items-center justify-center cursor-pointer"
                        >
                          <FaTrashAlt className="text-xs" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-400 font-medium text-xs italic">
                      No expense records found for the selected filter criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB C: Expense Catalog Management (Unfixed Prices) */}
      {activeTab === "catalog" && (
        <div className="space-y-6">
          {/* Information Notice */}
          <div className="bg-sky-50/70 border border-sky-200/80 rounded-2xl p-4 flex items-center gap-3.5 text-sky-800 text-left">
            <div className="h-8 w-8 rounded-xl bg-[#0096D1] text-white flex items-center justify-center shrink-0">
              <FaInfoCircle className="text-sm" />
            </div>
            <div className="text-xs">
              <p className="font-extrabold text-sky-900">
                Notice: Catalog items have no fixed prices
              </p>
              <p className="text-sky-700/90 mt-0.5 font-medium">
                Operating expenditures (fuel, maintenance, supplies, rent)
                fluctuate dynamically. The catalog establishes the classification
                heads; actual amounts are logged on a day-to-day basis.
              </p>
            </div>
          </div>

          {/* Catalog Cards Grid */}
          {isCatalogLoading ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center flex flex-col items-center justify-center space-y-3">
              <FaSpinner className="animate-spin text-2xl text-[#0096D1]" />
              <p className="text-xs font-bold text-slate-500">
                Loading expense catalog from server...
              </p>
            </div>
          ) : catalog.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center flex flex-col items-center justify-center space-y-3">
              <div className="h-12 w-12 rounded-2xl bg-sky-50 text-[#0096D1] flex items-center justify-center text-xl">
                <FaBoxes />
              </div>
              <h4 className="text-sm font-black text-slate-800">
                No Expense Categories Found
              </h4>
              <p className="text-xs text-slate-400 max-w-sm">
                Your school expense catalog is currently empty. Create your first
                operational spending head to start categorizing expenditures.
              </p>
              <button
                type="button"
                onClick={() => setOpenAddCatalogModal(true)}
                className="mt-2 px-4 py-2 rounded-xl bg-[#0096D1] hover:bg-[#0082b5] text-white text-xs font-bold shadow-xs transition-all flex items-center gap-2 cursor-pointer active:scale-95"
              >
                <FaPlus className="text-xs" />
                <span>Add Category Head</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {catalog.map((cat) => {
                const spentTotal = Number(cat.total_logged_outflow || 0);

                return (
                  <div
                    key={cat.id}
                    className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4 hover:border-slate-200 transition-all flex flex-col justify-between"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                            {cat.code}
                          </span>
                          {cat.icon_type &&
                            !STANDARD_ICON_TYPES.includes(cat.icon_type) && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 uppercase tracking-wide">
                                {cat.icon_type}
                              </span>
                            )}
                        </div>
                        <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {cat.is_active !== false ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-base shrink-0">
                          {getCategoryIcon(cat.icon_type || cat.iconType)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-base font-extrabold text-slate-800 leading-snug">
                            {cat.name}
                          </h4>
                          <p className="text-xs text-slate-400 font-medium leading-relaxed mt-1">
                            {cat.description || "No scope description provided"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                      <div>
                        <span className="block text-[10px] uppercase font-bold text-slate-400">
                          Logged Outflow
                        </span>
                        <span className="text-sm font-black text-slate-800 font-mono">
                          {formatNaira(spentTotal)}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleOpenEditCatalog(cat)}
                          className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-[#0096D1]/10 hover:border-[#0096D1]/30 hover:text-[#0096D1] text-slate-600 text-xs font-bold transition-all flex items-center gap-1 cursor-pointer active:scale-95"
                          title="Edit category details"
                        >
                          <FaEdit className="text-[11px]" />
                          <span>Edit</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCatalog(cat.id, cat.name)}
                          className="p-1.5 rounded-lg border border-rose-150 bg-rose-50/70 hover:bg-rose-100 text-rose-600 text-xs font-bold transition-all flex items-center justify-center cursor-pointer active:scale-95"
                          title="Delete category"
                        >
                          <FaTrashAlt className="text-[11px]" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB D: Suppliers & Vendors Directory */}
      {activeTab === "vendors" && (
        <div className="space-y-6">
          {/* Vendors Top Summary & Search Header */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center text-sm font-bold">
                <FaStore />
              </span>
              <div>
                <h3 className="text-sm font-extrabold text-slate-800">
                  Approved Vendors & Service Providers
                </h3>
                <p className="text-xs text-slate-400 font-medium">
                  {vendors.length} registered suppliers and service contractors
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => refetchVendors()}
                title="Refresh vendors from server"
                className="px-3.5 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 text-xs font-bold transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer active:scale-95"
              >
                <FaSync
                  className={`text-[11px] ${
                    isVendorsFetching ? "animate-spin text-[#0096D1]" : ""
                  }`}
                />{" "}
                Refresh
              </button>

              {/* Search Box */}
              <div className="relative flex-1 sm:w-64">
                <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input
                  type="text"
                  placeholder="Search supplier, contact, phone..."
                  value={vendorSearchQuery}
                  onChange={(e) => setVendorSearchQuery(e.target.value)}
                  className="w-full text-xs font-semibold pl-9 pr-3 py-2 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>

              <button
                type="button"
                onClick={() => setOpenAddVendorModal(true)}
                className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
              >
                <FaPlus className="text-[10px]" /> Add Vendor
              </button>
            </div>
          </div>

          {/* Vendors Grid */}
          {isVendorsLoading ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center flex flex-col items-center justify-center space-y-3">
              <FaSpinner className="text-2xl text-[#0096D1] animate-spin" />
              <p className="text-xs text-slate-500 font-semibold">
                Loading suppliers and vendors directory...
              </p>
            </div>
          ) : vendors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {vendors.map((vendor) => {
                const totalPaid = Number(vendor.total_outflows_paid || 0);
                const txCount = Number(vendor.transactions_count || 0);

                return (
                  <div
                    key={vendor.id}
                    className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4 hover:border-slate-200 transition-all flex flex-col justify-between"
                  >
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 uppercase">
                            {vendor.categoryTag}
                          </span>
                          <h4 className="text-base font-extrabold text-slate-800 mt-1">
                            {vendor.name}
                          </h4>
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleOpenEditVendor(vendor)}
                            className="p-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-[#0096D1]/10 hover:border-[#0096D1]/30 hover:text-[#0096D1] text-slate-500 text-xs font-bold transition-all cursor-pointer active:scale-95"
                            title="Edit supplier details"
                          >
                            <FaEdit className="text-[11px]" />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteVendor(vendor.id, vendor.name)
                            }
                            className="p-1.5 rounded-lg border border-rose-150 bg-rose-50/70 hover:bg-rose-100 text-rose-600 text-xs font-bold transition-all cursor-pointer active:scale-95"
                            title="Delete supplier"
                          >
                            <FaTrashAlt className="text-[11px]" />
                          </button>
                        </div>
                      </div>

                      <div className="space-y-1.5 text-xs text-slate-600 bg-slate-50/60 p-3 rounded-xl border border-slate-100">
                        {vendor.contactPerson && (
                          <div className="flex items-center gap-2">
                            <FaUserTie className="text-slate-400 text-[11px] shrink-0" />
                            <span className="font-semibold truncate">
                              {vendor.contactPerson}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <FaPhoneAlt className="text-slate-400 text-[11px] shrink-0" />
                          <span className="font-mono font-medium">
                            {vendor.phone}
                          </span>
                        </div>
                        {vendor.bankName && vendor.accountNumber && (
                          <div className="pt-1 border-t border-slate-200/60 text-[11px] font-medium text-slate-500">
                            <span className="font-bold text-slate-700">Bank:</span>{" "}
                            {vendor.bankName} •{" "}
                            <span className="font-mono font-bold text-slate-800">
                              {vendor.accountNumber}
                            </span>
                          </div>
                        )}
                        {vendor.address && (
                          <div className="text-[10px] text-slate-400 italic truncate">
                            📍 {vendor.address}
                          </div>
                        )}
                        {vendor.notes && (
                          <div className="text-[11px] text-slate-600 bg-white/80 rounded-lg p-2 border border-slate-200/60 mt-1">
                            <span className="font-bold text-slate-700">Note:</span> {vendor.notes}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                      <div>
                        <span className="block text-[10px] uppercase font-bold text-slate-400">
                          Disbursed Outflow
                        </span>
                        <span className="text-sm font-black text-slate-800 font-mono">
                          {formatNaira(totalPaid)}
                        </span>
                        <span className="text-[10px] text-slate-400 block">
                          {txCount} payment{txCount === 1 ? "" : "s"}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setSelectedVendorFilter(vendor.name);
                          setActiveTab("ledger");
                          toast.success(`Showing ledger transactions for ${vendor.name}`);
                        }}
                        className="px-3 py-1.5 rounded-lg border border-[#0096D1]/30 bg-[#0096D1]/10 hover:bg-[#0096D1] hover:text-white text-[#0096D1] text-xs font-bold transition-all cursor-pointer"
                        title="Filter ledger to see all transactions for this supplier"
                      >
                        View Outflows →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center text-slate-400 space-y-3">
              <FaStore className="text-3xl text-slate-300 mx-auto" />
              <p className="text-xs font-medium">
                No vendors or suppliers found matching your search.
              </p>
              <button
                type="button"
                onClick={() => setOpenAddVendorModal(true)}
                className="px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold shadow-sm cursor-pointer"
              >
                Add First Vendor
              </button>
            </div>
          )}
        </div>
      )}

      {/* MODAL 1: Log Daily Expense */}
      {openAddExpenseModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-2xl p-6 w-full max-w-lg text-left space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-black text-slate-800">
                  Log Daily Expenditure
                </h3>
                <p className="text-xs text-slate-400">
                  Record money spent on school operations
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpenAddExpenseModal(false)}
                className="text-slate-400 hover:text-slate-600 font-bold text-sm cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateExpense} className="space-y-4">
              {/* Category Picker */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Expense Category (Catalog Head)
                </label>
                <select
                  required
                  value={newExpenseForm.catalogId}
                  onChange={(e) =>
                    setNewExpenseForm({
                      ...newExpenseForm,
                      catalogId: e.target.value,
                    })
                  }
                  className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
                >
                  <option value="" disabled>-- Select Expense Category --</option>
                  {catalog.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount and Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Amount Spent (₦)
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    placeholder="e.g. 50000"
                    value={newExpenseForm.amount}
                    onChange={(e) =>
                      setNewExpenseForm({
                        ...newExpenseForm,
                        amount: e.target.value,
                      })
                    }
                    className="w-full text-xs font-bold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all font-mono"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Expense Date
                  </label>
                  <input
                    type="date"
                    required
                    value={newExpenseForm.expenseDate}
                    onChange={(e) =>
                      setNewExpenseForm({
                        ...newExpenseForm,
                        expenseDate: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all font-mono cursor-pointer"
                  />
                </div>
              </div>

              {/* Paid To & Payment Channel */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Paid To (Beneficiary / Vendor)
                  </label>
                  <select
                    required
                    value={newExpenseForm.vendorId}
                    onChange={(e) =>
                      setNewExpenseForm({
                        ...newExpenseForm,
                        vendorId: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
                  >
                    <option value="" disabled>
                      -- Select Vendor / Supplier --
                    </option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name} ({v.categoryTag})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Payment Channel
                  </label>
                  <select
                    value={newExpenseForm.paymentMethod}
                    onChange={(e) =>
                      setNewExpenseForm({
                        ...newExpenseForm,
                        paymentMethod: e.target.value as any,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
                  >
                    <option value="transfer">Bank Transfer</option>
                    <option value="cash">Cash Outflow</option>
                    <option value="pos">POS / Card</option>
                    <option value="cheque">Bank Cheque</option>
                  </select>
                </div>
              </div>

              {/* Description / Note */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Description & Receipt Notes
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. 50 Litres of diesel for campus generator during morning assembly..."
                  value={newExpenseForm.description}
                  onChange={(e) =>
                    setNewExpenseForm({
                      ...newExpenseForm,
                      description: e.target.value,
                    })
                  }
                  className="w-full text-xs font-medium p-3 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setOpenAddExpenseModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-[#0096D1] hover:bg-[#0082b5] text-white text-xs font-bold shadow-md transition-all active:scale-95 cursor-pointer"
                >
                  Record Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Create Catalog Head */}
      {openAddCatalogModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-2xl p-6 w-full max-w-md text-left space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-black text-slate-800">
                  New Catalog Spending Head
                </h3>
                <p className="text-xs text-slate-400">
                  Define category with no fixed price
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpenAddCatalogModal(false)}
                className="text-slate-400 hover:text-slate-600 font-bold text-sm cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="bg-sky-50/70 border border-sky-200/80 rounded-xl p-3 text-[11px] text-sky-800 flex items-center gap-2">
              <FaInfoCircle className="text-[#0096D1] shrink-0 text-xs" />
              <span>
                <b>Note:</b> A vendor is <b>not required</b> when creating a catalog head.
                Spending heads classify expense areas; suppliers are chosen dynamically during daily logging.
              </span>
            </div>

            <form onSubmit={handleCreateCatalogItem} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Category Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. ICT & Computer Lab Peripherals"
                  value={newCatalogForm.name}
                  onChange={(e) =>
                    setNewCatalogForm({
                      ...newCatalogForm,
                      name: e.target.value,
                    })
                  }
                  className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Classification Icon & Type
                </label>
                <select
                  value={newCatalogForm.icon_type}
                  onChange={(e) =>
                    setNewCatalogForm({
                      ...newCatalogForm,
                      icon_type: e.target.value,
                    })
                  }
                  className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
                >
                  <option value="projects">Projects / ICT / Lab Peripherals</option>
                  <option value="fuel">Fuel & Generator</option>
                  <option value="books">Books & Academic Materials</option>
                  <option value="maintenance">Facility Maintenance & Repairs</option>
                  <option value="rent">Facility & House Rent</option>
                  <option value="supplies">Sanitary & Toiletries Supplies</option>
                  <option value="transport">Vehicle & Bus Operations</option>
                  <option value="general">General Operating Outflow</option>
                  <option value="others">Other (Specify Custom Classification)</option>
                </select>
              </div>

              {newCatalogForm.icon_type === "others" && (
                <div className="space-y-1 animate-fade-in">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Describe Custom Classification / Type *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Science Lab Chemicals, Sports, Uniforms..."
                    value={newCatalogForm.other_type_description || ""}
                    onChange={(e) =>
                      setNewCatalogForm({
                        ...newCatalogForm,
                        other_type_description: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-amber-50/60 text-slate-800 rounded-xl border border-amber-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Scope Description
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="e.g. Keyboards, mice, HDMI cables, and network switches"
                  value={newCatalogForm.description}
                  onChange={(e) =>
                    setNewCatalogForm({
                      ...newCatalogForm,
                      description: e.target.value,
                    })
                  }
                  className="w-full text-xs font-medium p-3 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setOpenAddCatalogModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createCatalogMutation.isPending}
                  className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold shadow-md transition-all active:scale-95 cursor-pointer disabled:opacity-50"
                >
                  {createCatalogMutation.isPending ? "Creating..." : "Create Catalog Head"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: Edit Catalog Head */}
      {openEditCatalogModal && editingCatalogItem && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-2xl p-6 w-full max-w-md text-left space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-black text-slate-800">
                  Edit Spending Category Head
                </h3>
                <p className="text-xs text-slate-400">
                  Update category name, icon or description
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpenEditCatalogModal(false);
                  setEditingCatalogItem(null);
                }}
                className="text-slate-400 hover:text-slate-600 font-bold text-sm cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveEditCatalog} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Category Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Diesel & Generator Fuel"
                  value={editCatalogForm.name}
                  onChange={(e) =>
                    setEditCatalogForm({
                      ...editCatalogForm,
                      name: e.target.value,
                    })
                  }
                  className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Classification Icon & Type
                </label>
                <select
                  value={editCatalogForm.icon_type}
                  onChange={(e) =>
                    setEditCatalogForm({
                      ...editCatalogForm,
                      icon_type: e.target.value,
                    })
                  }
                  className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
                >
                  <option value="projects">Projects / ICT / Lab Peripherals</option>
                  <option value="fuel">Fuel & Generator</option>
                  <option value="books">Books & Academic Materials</option>
                  <option value="maintenance">Facility Maintenance & Repairs</option>
                  <option value="rent">Facility & House Rent</option>
                  <option value="supplies">Sanitary & Toiletries Supplies</option>
                  <option value="transport">Vehicle & Bus Operations</option>
                  <option value="general">General Operating Outflow</option>
                  <option value="others">Other (Specify Custom Classification)</option>
                </select>
              </div>

              {editCatalogForm.icon_type === "others" && (
                <div className="space-y-1 animate-fade-in">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Describe Custom Classification / Type *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Science Lab Chemicals, Sports, Uniforms..."
                    value={editCatalogForm.other_type_description || ""}
                    onChange={(e) =>
                      setEditCatalogForm({
                        ...editCatalogForm,
                        other_type_description: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-amber-50/60 text-slate-800 rounded-xl border border-amber-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Scope Description
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="Describe expenditures covered under this head..."
                  value={editCatalogForm.description}
                  onChange={(e) =>
                    setEditCatalogForm({
                      ...editCatalogForm,
                      description: e.target.value,
                    })
                  }
                  className="w-full text-xs font-medium p-3 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    setOpenEditCatalogModal(false);
                    setEditingCatalogItem(null);
                  }}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateCatalogMutation.isPending}
                  className="px-5 py-2.5 rounded-xl bg-[#0096D1] hover:bg-[#0082b5] text-white text-xs font-bold shadow-md transition-all active:scale-95 cursor-pointer disabled:opacity-50"
                >
                  {updateCatalogMutation.isPending ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* MODAL 4: Create Vendor / Supplier */}
      {openAddVendorModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-2xl p-6 w-full max-w-lg text-left space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-black text-slate-800">
                  Register New Supplier / Contractor
                </h3>
                <p className="text-xs text-slate-400">
                  Add approved service vendor to directory
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpenAddVendorModal(false)}
                className="text-slate-400 hover:text-slate-600 font-bold text-sm cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateVendor} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Supplier / Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. TotalEnergies Lekki"
                    value={newVendorForm.name}
                    onChange={(e) =>
                      setNewVendorForm({
                        ...newVendorForm,
                        name: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Category Tag / Specialization
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Fuel, Mechanical, Sanitary"
                    value={newVendorForm.categoryTag}
                    onChange={(e) =>
                      setNewVendorForm({
                        ...newVendorForm,
                        categoryTag: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Contact Phone Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 0803 123 4567"
                    value={newVendorForm.phone}
                    onChange={(e) =>
                      setNewVendorForm({
                        ...newVendorForm,
                        phone: e.target.value,
                      })
                    }
                    className="w-full text-xs font-mono font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Contact Person Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Mr. Kunle Adeyemi"
                    value={newVendorForm.contactPerson}
                    onChange={(e) =>
                      setNewVendorForm({
                        ...newVendorForm,
                        contactPerson: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. GTBank, Access Bank"
                    value={newVendorForm.bankName}
                    onChange={(e) =>
                      setNewVendorForm({
                        ...newVendorForm,
                        bankName: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Account Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 0123456789"
                    value={newVendorForm.accountNumber}
                    onChange={(e) =>
                      setNewVendorForm({
                        ...newVendorForm,
                        accountNumber: e.target.value,
                      })
                    }
                    className="w-full text-xs font-mono font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Physical Location / Office Address
                </label>
                <input
                  type="text"
                  placeholder="e.g. Lekki Expressway, Victoria Island, Lagos"
                  value={newVendorForm.address}
                  onChange={(e) =>
                    setNewVendorForm({
                      ...newVendorForm,
                      address: e.target.value,
                    })
                  }
                  className="w-full text-xs font-medium px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Supplier Notes / Description
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Handles examination sheets printing, credit terms 30 days..."
                  value={newVendorForm.notes}
                  onChange={(e) =>
                    setNewVendorForm({
                      ...newVendorForm,
                      notes: e.target.value,
                    })
                  }
                  className="w-full text-xs font-medium p-3 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setOpenAddVendorModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createVendorMutation.isPending}
                  className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-black text-white text-xs font-bold shadow-md transition-all active:scale-95 cursor-pointer disabled:opacity-50"
                >
                  {createVendorMutation.isPending ? "Registering..." : "Register Supplier"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 5: Edit Vendor / Supplier */}
      {openEditVendorModal && editingVendorItem && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-2xl p-6 w-full max-w-lg text-left space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-black text-slate-800">
                  Edit Supplier / Contractor Details
                </h3>
                <p className="text-xs text-slate-400">
                  Update bank info, phone or category
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpenEditVendorModal(false);
                  setEditingVendorItem(null);
                }}
                className="text-slate-400 hover:text-slate-600 font-bold text-sm cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveEditVendor} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Supplier / Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={editVendorForm.name}
                    onChange={(e) =>
                      setEditVendorForm({
                        ...editVendorForm,
                        name: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Category Tag / Specialization
                  </label>
                  <input
                    type="text"
                    value={editVendorForm.categoryTag}
                    onChange={(e) =>
                      setEditVendorForm({
                        ...editVendorForm,
                        categoryTag: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Contact Phone Number
                  </label>
                  <input
                    type="text"
                    value={editVendorForm.phone}
                    onChange={(e) =>
                      setEditVendorForm({
                        ...editVendorForm,
                        phone: e.target.value,
                      })
                    }
                    className="w-full text-xs font-mono font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Contact Person Name
                  </label>
                  <input
                    type="text"
                    value={editVendorForm.contactPerson}
                    onChange={(e) =>
                      setEditVendorForm({
                        ...editVendorForm,
                        contactPerson: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={editVendorForm.bankName}
                    onChange={(e) =>
                      setEditVendorForm({
                        ...editVendorForm,
                        bankName: e.target.value,
                      })
                    }
                    className="w-full text-xs font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Account Number
                  </label>
                  <input
                    type="text"
                    value={editVendorForm.accountNumber}
                    onChange={(e) =>
                      setEditVendorForm({
                        ...editVendorForm,
                        accountNumber: e.target.value,
                      })
                    }
                    className="w-full text-xs font-mono font-semibold px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Physical Location / Office Address
                </label>
                <input
                  type="text"
                  value={editVendorForm.address}
                  onChange={(e) =>
                    setEditVendorForm({
                      ...editVendorForm,
                      address: e.target.value,
                    })
                  }
                  className="w-full text-xs font-medium px-3.5 py-2.5 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Supplier Notes / Description
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Handles examination sheets printing, credit terms 30 days..."
                  value={editVendorForm.notes}
                  onChange={(e) =>
                    setEditVendorForm({
                      ...editVendorForm,
                      notes: e.target.value,
                    })
                  }
                  className="w-full text-xs font-medium p-3 bg-slate-50 text-slate-800 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    setOpenEditVendorModal(false);
                    setEditingVendorItem(null);
                  }}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateVendorMutation.isPending}
                  className="px-5 py-2.5 rounded-xl bg-[#0096D1] hover:bg-[#0082b5] text-white text-xs font-bold shadow-md transition-all active:scale-95 cursor-pointer disabled:opacity-50"
                >
                  {updateVendorMutation.isPending ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseManagement;
